import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { Check, X, Eye } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils";
import { formatLocaleDate } from "@/utils/commonUtils";
import { isFeatureEnabled } from "@/utils/commonUtils";
import {
  Typography,
  Button,
  TooltipComp,
  PortalTooltipComp,
} from "@/components/ui";
import { Spinner } from "flowbite-react";
import { TEvaluationDataItem, TEvaluationItem } from "@/api/evaluation-listing";
import { TStrictColumnDef } from "@/pages/app/evaluation-list/EvaluationList";
import ScoreCell from "@/components/common/ScoreCell";
import { usePermissions } from "@/hooks/usePermissions";
import { TSelectOption } from "@/components/forms/Select";

/**
 * Shared transform function for evaluation data
 * Converts API response to table row data
 */
export const transformEvaluationData = (
  apiData: TEvaluationItem[],
): TEvaluationDataItem[] => {
  return apiData?.map((item) => ({
    workflow_status: item?.current_workflow_level_status?.display_name ?? "-",
    id: item?.internal_id,
    agent_id: item?.entity_agent?.internal_id ?? "",
    agent_name:
      (item?.entity_agent?.first_name ?? item?.entity_agent?.last_name)
        ? `${item?.entity_agent?.first_name ?? ""} ${item?.entity_agent?.last_name ?? ""}`.trim()
        : "-",
    ai_score: item?.ai_score,
    qa_score: item?.qa_score,
    date_of_call: item?.recording?.call_date,
    audit_date: item?.created_on?.split("T")[0] ?? "",
    ai_review: item?.ai_compliance,
    offensive_word_found: item?.is_offensive_word_found ? "Yes" : "No",
    campaign: item?.campaign?.name ?? "-",
    campaign_id: item?.campaign?.internal_id ?? "",
    supervisor_id: item?.auditor?.internal_id ?? "",
    supervisor_name:
      item?.auditor?.first_name || item?.auditor?.last_name
        ? `${item?.auditor?.first_name ?? ""} ${item?.auditor?.last_name ?? ""}`.trim()
        : "-",
    service: item?.service?.name ?? "-",
    service_id: item?.service?.internal_id ?? "",
    call_type: item?.recording?.external_service_call_type,
    action: "view",
    dispute: item?.dispute ?? "",
    is_profane: item?.is_profane,
  }));
};

const yesNoOptions: TSelectOption[] = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const yesNoValueOptions: TSelectOption[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];
const reviewStatusOptions: TSelectOption[] = [
  { value: "true", label: "Compliant" },
  { value: "false", label: "Non-Compliant" },
];

/**
 * Hook to get evaluation list columns
 * Can be customized with optional parameters
 */
export const useEvaluationListColumns = (options?: {
  onRowClick?: (evaluationId: string) => void;
  loadingEvaluationId?: string | null;
}): TStrictColumnDef<TEvaluationDataItem>[] => {
  const columnHelper = createColumnHelper<TEvaluationDataItem>();
  const { t } = useTranslation(["evaluationListingPage", "common"]);
  const { hasAnyPermission } = usePermissions();
  const canViewDetails = hasAnyPermission(["can-view-ai-evaluation-details"]);

  return useMemo(
    () =>
      [
        columnHelper.accessor("agent_name", {
          id: "agent_name",
          header: t("EVALUATION_LISTING.TABLE_HEADER.AGENT_NAME"),
          cell: ({ getValue }) => (
            <div className="text-left">
              {getValue() && getValue() !== "-" ? (
                <>
                  <Typography
                    variant={"article"}
                    title={getValue()}
                    className={cn("text-sm truncate")}
                  >
                    {getValue()}
                  </Typography>
                </>
              ) : (
                <Typography
                  variant={"article"}
                  className="text-sm pl-3 md:pl-3 lg:pl-4 xl:pl-6"
                >
                  {t("common:N_A")}
                </Typography>
              )}
            </div>
          ),
          size: 150,
          meta: {
            type: "agent_name",
            excludeFromSearch: true,
          },
          enableSorting: true,
        }),

        columnHelper.accessor("ai_score", {
          id: "ai_score",
          header: `${t("EVALUATION_LISTING.TABLE_HEADER.AI_SCORE")} (%)`,
          cell: ({ getValue }) => (
            <div className="text-center">
              <ScoreCell value={getValue()} />
            </div>
          ),
          size: 150,
          meta: {
            type: "ai_score",
            thClass: "justify-center w-full",
          },
          enableSorting: true,
        }),

        columnHelper.accessor("qa_score", {
          id: "qa_score",
          header: `${t("EVALUATION_LISTING.TABLE_HEADER.QA_SCORE")} (%)`,
          cell: ({ getValue }) => (
            <div className="text-center">
              <ScoreCell value={getValue()} />
            </div>
          ),
          size: 150,
          meta: {
            type: "qa_score",
            thClass: "justify-center w-full",
          },
          enableSorting: true,
        }),

        columnHelper.accessor("call_type", {
          id: "call_type",
          header: t("EVALUATION_LISTING.TABLE_HEADER.CALL_TYPE"),
          cell: ({ getValue }) => {
            const callType = getValue() ?? t("common:N_A");
            return (
              <div className="text-left">
                {callType && callType !== "-" ? (
                  <PortalTooltipComp
                    content={callType}
                    placement="top"
                    trigger="hover"
                  >
                    <Typography variant={"p"} className={"text-sm truncate"}>
                      {callType}
                    </Typography>
                  </PortalTooltipComp>
                ) : (
                  <Typography variant={"article"} className="text-sm">
                    {callType}
                  </Typography>
                )}
              </div>
            );
          },
          size: 120,
          meta: {
            type: "qa_score",
            thClass: "w-full text-left",
          },
          enableSorting: true,
        }),

        columnHelper.accessor("date_of_call", {
          id: "call_date",
          header: t("EVALUATION_LISTING.TABLE_HEADER.DATE_OF_CALL"),
          cell: ({ getValue }) => {
            const evalDate = new Date(getValue());
            return (
              <Typography variant={"article"} className="text-sm">
                {formatLocaleDate(evalDate)}
              </Typography>
            );
          },
          size: 130,
          meta: {
            type: "date_of_call",
            thClass: "justify-start w-full",
            filterSearchType: "date",
          },
          enableSorting: true,
        }),

        columnHelper.accessor("audit_date", {
          id: "created_on",
          header: t("EVALUATION_LISTING.TABLE_HEADER.AUDIT_DATE"),
          cell: ({ getValue }) => {
            const evalDate = new Date(getValue());
            return (
              <Typography variant={"article"} className="text-sm">
                {formatLocaleDate(evalDate)}
              </Typography>
            );
          },
          size: 130,
          meta: {
            type: "audit_date",
            thClass: "justify-start w-full",
            filterSearchType: "date",
          },
          enableSorting: true,
        }),

        columnHelper.accessor("ai_review", {
          id: "ai_compliance",
          header: t("EVALUATION_LISTING.TABLE_HEADER.AI_REVIEW"),
          size: 170,
          cell: ({ getValue }) => {
            const value = getValue();
            const isCompliant = value;

            const chipClass =
              isCompliant === true
                ? "bg-green/10 text-green"
                : isCompliant === false
                  ? "bg-destructive/10 text-destructive dark bg-destructive/11 brightness-90"
                  : "bg-gray/10 text-dark/70";

            return (
              <div className="flex items-center justify-center w-full">
                <Typography
                  variant={"article"}
                  affects={"medium"}
                  className={cn(
                    "text-xs inline-flex items-center gap-1 rounded-full capitalize badge-custom px-2 py-1",
                    chipClass,
                  )}
                >
                  {isCompliant === true ? (
                    <Check className="text-green" size={14} />
                  ) : isCompliant === false ? (
                    <X className="text-destructive" size={14} />
                  ) : (
                    ""
                  )}
                  <span>
                    {isCompliant === true
                      ? "Compliant"
                      : isCompliant === false
                        ? "Non-Compliant"
                        : "Yet to be review"}
                  </span>
                </Typography>
              </div>
            );
          },
          meta: {
            type: "ai_review",
            thClass: "justify-center w-full",
            filterSearchType: "dropdown",
            filterSearchOptions: reviewStatusOptions,
          },
          enableSorting: true,
        }),

        columnHelper.accessor("dispute", {
          id: "dispute",
          header: t("EVALUATION_LISTING.TABLE_HEADER.DISPUTED"),
          cell: ({ getValue }) => {
            const value = getValue();

            const chipClass =
              value === "No"
                ? "bg-muted/10 text-muted-foreground dark:bg-muted/20 brightness-90"
                : "bg-green/10 text-green";

            return (
              <div className="flex items-center justify-center w-full">
                <Typography
                  variant={"article"}
                  affects={"semiBold"}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-md text-xs capitalize badge-custom",
                    chipClass,
                  )}
                >
                  {value ?? t("common:N_A")}
                </Typography>
              </div>
            );
          },
          size: 100,
          meta: {
            type: "dispute",
            thClass: "justify-center w-full",
            filterSearchType: "dropdown",
            filterSearchOptions: yesNoValueOptions,
          },
          enableSorting: true,
        }),

        columnHelper.accessor("campaign", {
          id: "campaign",
          header: t("EVALUATION_LISTING.TABLE_HEADER.CAMPAIGN"),
          cell: ({ getValue }) => (
            <div className="text-left">
              <Typography
                variant={"article"}
                className="text-sm whitespace-normal break-keep"
              >
                {getValue() ?? t("common:N_A")}
              </Typography>
            </div>
          ),
          meta: { type: "campaign", excludeFromSearch: true },
          enableSorting: true,
          size: 150,
        }),

        columnHelper.accessor("service", {
          id: "service",
          header: t("EVALUATION_LISTING.TABLE_HEADER.SERVICE"),
          cell: ({ getValue }) => (
            <div className="text-left">
              <Typography
                variant={"article"}
                className="text-sm whitespace-normal break-keep"
              >
                {getValue() ?? t("common:N_A")}
              </Typography>
            </div>
          ),
          meta: { type: "service", excludeFromSearch: false },
          enableSorting: true,
        }),
        columnHelper.accessor("is_profane", {
          id: "is_profane",
          header: t("EVALUATION_LISTING.TABLE_HEADER.PROFANITY_DETECTED"),
          cell: ({ getValue }) => {
            const value = getValue() === true ? "Yes" : "No";

            const chipClass =
              value === "No"
                ? "bg-muted/10 text-muted-foreground dark:bg-muted/20 brightness-90"
                : "bg-destructive/10 text-destructive";

            return (
              <div className="flex items-center justify-center w-full">
                <Typography
                  variant={"article"}
                  affects={"semiBold"}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-md text-xs capitalize badge-custom",
                    chipClass,
                  )}
                >
                  {value ?? t("common:N_A")}
                </Typography>
              </div>
            );
          },
          size: 100,
          meta: {
            type: "is_profane",
            thClass: "justify-center w-full",
            filterSearchType: "dropdown",
            filterSearchOptions: yesNoOptions,
          },
          enableSorting: false,
        }),

        columnHelper.accessor("workflow_status", {
          id: "workflow_status",
          header: t("EVALUATION_LISTING.TABLE_HEADER.WORKFLOW_STATUS"),
          cell: ({ getValue }) => {
            const status = getValue() ?? t("common:N_A");
            return (
              <div className="text-left">
                {status && status !== "-" ? (
                  <PortalTooltipComp
                    content={status}
                    placement="top"
                    trigger="hover"
                  >
                    <Typography variant={"p"} className={"text-sm truncate"}>
                      {status}
                    </Typography>
                  </PortalTooltipComp>
                ) : (
                  <Typography variant={"p"} className="text-center">
                    {status}
                  </Typography>
                )}
              </div>
            );
          },
          size: 150,
          meta: {
            type: "status",
            thClass: "w-full text-left",
          },
          enableSorting: false,
        }),

        // Only include action column if onRowClick is provided
        ...(options?.onRowClick &&
        isFeatureEnabled("evaluation-details") &&
        canViewDetails
          ? [
              columnHelper.accessor("action", {
                id: "actions",
                header: () => (
                  <Typography
                    variant={"article"}
                    className="text-sm justify-center"
                    affects={"semiBold"}
                  >
                    {t("EVALUATION_LISTING.TABLE_HEADER.ACTION")}
                  </Typography>
                ),
                cell: ({ row }) => {
                  const evaluationId = row.original.id;
                  const isDetailLoading =
                    options?.loadingEvaluationId === evaluationId;
                  const showViewIcon =
                    isFeatureEnabled("evaluation-details") && canViewDetails;
                  return (
                    <div className="flex items-center justify-center">
                      {showViewIcon && (
                        <TooltipComp content="View" placement="left">
                          <Button
                            variant="action"
                            size="icon"
                            className="p-2 cursor-pointer rounded-full hover:text-green"
                            onClick={() => options.onRowClick?.(evaluationId)}
                            id={`evaluation-list-view-button-${evaluationId}`}
                          >
                            {isDetailLoading ? (
                              <Spinner size={"sm"} />
                            ) : (
                              <Eye size={16} aria-label="View" />
                            )}
                          </Button>
                        </TooltipComp>
                      )}
                    </div>
                  );
                },
                meta: { type: "action", thClass: "justify-center w-full" },
                enableSorting: false,
                size: 90,
              }),
            ]
          : []),
      ] as TStrictColumnDef<TEvaluationDataItem>[],
    [columnHelper, t, options, canViewDetails],
  );
};
